import { useRef, useState } from 'react'
import { Button, Input, Loading } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { designAssetsAtom, pageLoadingAtom, themeOverrideAtom } from 'atoms'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { useActions } from '@/hooks/editor/useActions'
import { FileUploadHandler, useFileUpload } from './'
import { useTranslation } from 'react-i18next'
import { getDesignAssets, uploadDesignAsset } from '@/api/designAssets'

const DesignAssetModal = (props) => {
  const modalProps = useDesignAssetModal(props)
  const { error } = modalProps

  let ModalState = null
  if (error) ModalState = Error
  if (!error) ModalState = Content

  return (
    <Flex column full padding="32px">
      <Text size="20px" weight="600" margin="10px 0 10px 0">
        {modalProps.t('editor.designAssetModal.designAssetName')}
      </Text>
      <ModalState {...modalProps} />
    </Flex>
  )
}

const Content = (props) => {
  const {
    name,
    setName,
    closeModal,
    loading,
    inputFileDesignAsset,
    handleDesignAssetUpload,
    clickDesignAssetUpload,
    t,
  } = props

  return (
    <Loading isLoading={loading}>
      <Flex column gap="12px">
        <Input
          value={[name, setName]}
          placeholder={t('editor.designAssetModal.placeholder')}
          width="100%"
        />
        <Flex center margin="10px 0 0 0">
          <Button margin="0 16px 0 0" onClick={closeModal}>
            {t('editor.designAssetModal.cancelButton')}
          </Button>
          <Button primary margin="0 16px 0 0" onClick={clickDesignAssetUpload}>
            {t('editor.designAssetModal.uploadButton')}
          </Button>
        </Flex>
      </Flex>
      <FileUploadHandler
        inputRef={inputFileDesignAsset}
        onChange={handleDesignAssetUpload}
      />
    </Loading>
  )
}

const Error = (props) => {
  const { error, closeModal, t } = props

  return (
    <Flex column gap="12px" maxWidth="300px">
      <Text>{t('editor.designAssetModal.errorText', { error })}</Text>
      <Button margin="0 16px 0 0" onClick={closeModal}>
        {t('editor.designAssetModal.closeButton')}
      </Button>
    </Flex>
  )
}

export const useDesignAssetModal = (props) => {
  const { setShowDesignAssetModal } = props
  const { t } = useTranslation()

  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const inputFileDesignAsset = useRef(null)

  const handleError = useErrorHandler('DesignAssetModal')
  const action = useActions()
  const themeOverride = useRecoilValue(themeOverrideAtom)
  const setDesignAssets = useSetRecoilState(designAssetsAtom)
  const setPageLoading = useSetRecoilState(pageLoadingAtom)

  const closeModal = () => {
    setShowDesignAssetModal(false)
  }

  const clickDesignAssetUpload = () => {
    inputFileDesignAsset.current.click()
  }

  const uploadComplete = () => {
    setPageLoading(false)
    setLoading(false)
    setName('')
    closeModal()
  }

  const uploadFailed = (error) => {
    setPageLoading(false)
    setLoading(false)
    setName('')
    setError(error)
  }

  const uploadFunction = (fd) => {
    try {
      setLoading(true)
      setPageLoading(true)
      fd.append('name', name)
      return uploadDesignAsset(themeOverride.theme_id, fd)
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
      uploadFailed(error)
    }
  }

  const setImages = async (data) => {
    try {
      const designAssetsData = await getDesignAssets(themeOverride.theme_id)
      setDesignAssets(designAssetsData.data.designAssets)
      await action.insertDesignAsset({
        designAsset: designAssetsData.data.designAssets.length - 1,
        url: data.url,
      })
      uploadComplete()
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
      uploadFailed(error)
    }
  }

  const handleDesignAssetUpload = useFileUpload({ uploadFunction, setImages })

  return {
    name,
    setName,
    closeModal,
    loading,
    error,
    inputFileDesignAsset,
    handleDesignAssetUpload,
    clickDesignAssetUpload,
    t,
  }
}

export default DesignAssetModal
